import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta, SITE_URL } from "@/lib/seo";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { LetsTalkForm } from "@/components/LetsTalkForm";
import { CASE_STUDIES, findCaseStudy, type CaseStudy } from "@/data/caseStudies";
import { ArrowLeft, ArrowRight, Check, MapPin, Phone, Star } from "lucide-react";

export const Route = createFileRoute("/case-studies/$slug")({
  loader: ({ params }) => {
    const study = findCaseStudy(params.slug);
    if (!study) throw notFound();
    return { study };
  },
  head: (ctx) => {
    const study = ctx.loaderData?.study;
    if (!study) {
      return {
        links: [canonicalLink(`/case-studies/${ctx.params.slug}`)],
        meta: routeMeta(ctx, {
          title: "Case Study — Texas Made Shade",
          description: "Texas Made Shade project case study. Pergola covers and welded aluminum frames built in Seguin, TX.",
        }),
      };
    }
    const url = canonicalUrl(`/case-studies/${study.slug}`);
    return {
      links: [canonicalLink(`/case-studies/${study.slug}`)],
      meta: routeMeta(ctx, {
        title: `${study.title} | Texas Made Shade Case Study`,
        description: study.summary,
        image: study.images[0]?.src,
        imageAlt: study.images[0]?.alt,
        socialTitle: study.title,
        socialDescription: study.summary,
        type: "article",
        keywords: `${study.service}, ${study.location}, Texas Made Shade case study, ${study.category}`,
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "@id": `${url}#article`,
            headline: study.title,
            description: study.summary,
            datePublished: `${study.year}-01-01`,
            author: { "@type": "Organization", name: "Texas Made Shade & Covers" },
            publisher: { "@id": `${SITE_URL}/#business` },
            mainEntityOfPage: url,
            image: study.images.map((img) => `${SITE_URL}${img.src}`),
            about: study.service,
            locationCreated: { "@type": "Place", name: study.location },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: "Case Studies", item: `${SITE_URL}/case-studies` },
              { "@type": "ListItem", position: 3, name: study.title, item: url },
            ],
          }),
        },
      ],
    };
  },
  component: CaseStudyDetail,
  notFoundComponent: () => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center lg:px-8">
        <h1 className="font-display text-4xl font-bold text-navy-deep">Case study not found</h1>
        <p className="mt-4 text-navy-deep/75">That project doesn't exist (yet).</p>
        <Link
          to="/case-studies"
          className="mt-8 inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm uppercase tracking-wider text-white"
        >
          <ArrowLeft className="h-4 w-4" /> Back to Case Studies
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }) => (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1} className="mx-auto max-w-3xl px-4 py-24 text-center lg:px-8">
        <h1 className="font-display text-3xl font-bold text-navy-deep">Something went wrong</h1>
        <p className="mt-4 text-navy-deep/75">{(error as Error)?.message}</p>
      </main>
      <SiteFooter />
    </div>
  ),
});

function CaseStudyDetail() {
  const { slug } = Route.useParams();
  const study = findCaseStudy(slug) as CaseStudy;
  const others = CASE_STUDIES.filter((c) => c.slug !== study.slug).slice(0, 3);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main id="main-content" tabIndex={-1}>
        {/* HERO */}
        <section className="relative overflow-hidden bg-navy-deep text-white">
          <div className="absolute inset-0">
            <img
              src={study.images[0]?.src}
              alt={study.images[0]?.alt ?? study.title}
              className="h-full w-full object-cover opacity-40"
              width={1920}
              height={1080}
              loading="eager"
              fetchPriority="high"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/80 to-navy-deep/40" />
          </div>
          <div className="relative mx-auto max-w-7xl px-4 py-16 lg:px-8 lg:py-24">
            <Link
              to="/case-studies"
              className="inline-flex items-center gap-2 font-display text-xs uppercase tracking-[0.25em] text-white/75 hover:text-white"
            >
              <ArrowLeft className="h-3 w-3" /> All Case Studies
            </Link>
            <div className="mt-6 inline-flex items-center gap-2 bg-red px-3 py-1 font-display text-[11px] uppercase tracking-[0.3em]">
              ★ {study.category} · {study.year}
            </div>
            <h1 className="mt-4 max-w-4xl font-display text-3xl font-bold leading-[0.95] sm:text-5xl md:text-6xl">
              {study.title}
            </h1>
            <p className="mt-5 max-w-3xl text-lg text-white/85 sm:text-xl">{study.summary}</p>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-red" /> {study.location}
              </span>
              <span className="hidden h-4 w-px bg-white/30 sm:inline-block" />
              <span>
                Service:{" "}
                <Link to={study.serviceHref} className="underline decoration-red decoration-2 underline-offset-4 hover:text-white">
                  {study.service}
                </Link>
              </span>
              {study.cityHref && (
                <>
                  <span className="hidden h-4 w-px bg-white/30 sm:inline-block" />
                  <span>
                    City:{" "}
                    <Link to={study.cityHref} className="underline decoration-red decoration-2 underline-offset-4 hover:text-white">
                      {study.location.split(",")[0]}
                    </Link>
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="stripe-bar absolute bottom-0 left-0 h-2 w-full" />
        </section>

        {/* OVERVIEW + SPECS */}
        <section className="bg-cream py-16 lg:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-3 lg:px-8">
            <div className="lg:col-span-2">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Project Overview</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                The Build
              </h2>
              {study.overview.map((p, i) => (
                <p key={i} className="mt-5 text-lg text-navy-deep/80">
                  {p}
                </p>
              ))}

              <div className="mt-10">
                <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Scope of Work</div>
                <ul className="mt-4 space-y-2">
                  {study.scope.map((s, i) => (
                    <li key={i} className="flex items-start gap-2 text-navy-deep">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-red" />
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <aside className="border-2 border-navy-deep/15 bg-white shadow-card">
              <div className="border-b-2 border-navy-deep/10 bg-navy-deep px-5 py-3 font-display text-xs uppercase tracking-[0.3em] text-white">
                ★ Materials & Specs
              </div>
              <dl className="divide-y divide-navy-deep/10">
                {study.specs.map((s) => (
                  <div key={s.label} className="px-5 py-3">
                    <dt className="font-display text-[10px] uppercase tracking-[0.25em] text-red">
                      {s.label}
                    </dt>
                    <dd className="mt-1 text-sm font-semibold text-navy-deep">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </aside>
          </div>
        </section>

        {/* GALLERY */}
        <section className="border-t-4 border-navy-deep bg-white py-16 lg:py-20">
          <div className="mx-auto max-w-7xl px-4 lg:px-8">
            <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ On-Site & In The Shop</div>
            <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">Project Photos</h2>
            <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {study.images.map((img, i) => (
                <figure key={i} className="border-2 border-navy-deep/10 bg-cream shadow-card">
                  <img
                    src={img.src}
                    alt={img.alt}
                    width={1200}
                    height={900}
                    loading="lazy"
                    decoding="async"
                    className="block aspect-[4/3] w-full object-cover"
                  />
                  {img.caption && (
                    <figcaption className="border-t border-navy-deep/10 px-4 py-3 text-sm text-navy-deep/75">
                      {img.caption}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>

        {/* OUTCOMES */}
        <section className="bg-navy-deep py-16 text-white lg:py-20">
          <div className="mx-auto grid max-w-7xl gap-10 px-4 lg:grid-cols-2 lg:px-8">
            <div>
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Results</div>
              <h2 className="mt-2 font-display text-3xl font-bold md:text-4xl">Outcomes</h2>
              <ul className="mt-6 space-y-3">
                {study.outcomes.map((o, i) => (
                  <li key={i} className="flex items-start gap-2 text-lg text-white/90">
                    <Check className="mt-1 h-5 w-5 shrink-0 text-red" />
                    <span>{o}</span>
                  </li>
                ))}
              </ul>
            </div>

            {study.testimonial && (
              <figure className="border-l-4 border-red bg-white/5 p-6 backdrop-blur-sm">
                <div className="flex gap-0.5 text-red" aria-hidden="true">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-current" />
                  ))}
                </div>
                <blockquote className="mt-3 font-display text-xl italic leading-snug">
                  "{study.testimonial.quote}"
                </blockquote>
                <figcaption className="mt-4 font-display text-xs uppercase tracking-wider text-white/70">
                  — {study.testimonial.author}
                  {study.testimonial.role ? ` · ${study.testimonial.role}` : ""}
                </figcaption>
              </figure>
            )}
          </div>

          {/* CTA */}
          <div className="mx-auto mt-12 flex max-w-7xl flex-col items-center gap-4 border-t border-white/15 px-4 pt-10 sm:flex-row sm:justify-between lg:px-8">
            <div>
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ Want Something Like This?</div>
              <div className="mt-2 font-display text-2xl font-bold uppercase">
                Get a free, no-pressure estimate.
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              <a
                href="#lets-talk"
                className="inline-flex items-center gap-2 bg-red px-6 py-3 font-display text-sm uppercase tracking-wider text-white shadow-bold transition hover:bg-red-deep"
              >
                <ArrowRight className="h-4 w-4" /> Get a Free Estimate
              </a>
              <a
                href="tel:+18303909588"
                className="inline-flex items-center gap-2 bg-white px-6 py-3 font-display text-sm uppercase tracking-wider text-navy-deep shadow-bold transition hover:bg-cream"
              >
                <Phone className="h-4 w-4" /> +1 (830) 390-9588
              </a>
            </div>
          </div>
        </section>

        {/* OTHER CASE STUDIES */}
        {others.length > 0 && (
          <section className="bg-cream py-16 lg:py-20">
            <div className="mx-auto max-w-7xl px-4 lg:px-8">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-red">★ More Field Reports</div>
              <h2 className="mt-2 font-display text-3xl font-bold text-navy-deep md:text-4xl">
                Other Case Studies
              </h2>
              <div className="mt-8 grid gap-8 md:grid-cols-3">
                {others.map((c) => (
                  <Link
                    key={c.slug}
                    to="/case-studies/$slug"
                    params={{ slug: c.slug }}
                    className="group block overflow-hidden border-2 border-navy-deep/15 bg-white shadow-card transition hover:border-red hover:shadow-bold"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-navy-deep">
                      <img
                        src={c.images[0]?.src}
                        alt={c.images[0]?.alt ?? c.title}
                        loading="lazy"
                        decoding="async"
                        width={800}
                        height={600}
                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-5">
                      <div className="font-display text-[10px] uppercase tracking-[0.25em] text-red">
                        {c.category} · {c.location}
                      </div>
                      <h3 className="mt-2 font-display text-base font-bold leading-tight text-navy-deep group-hover:text-red">
                        {c.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        <LetsTalkForm />
      </main>
      <SiteFooter />
    </div>
  );
}
